export const GOOGLE_AUTH_WINDOW_NAME = "meeting-radar-google-auth";
export const OAUTH_COMPLETION_PARAM = "meeting-radar-oauth";
export const OAUTH_COMPLETION_MESSAGE = "meeting-radar:oauth-complete";

type AuthWindow = {
  closed: boolean;
  close: () => void;
  focus?: () => void;
  location: { href: string };
};

type CompletionWindow = {
  close: () => void;
  history: { replaceState: (data: unknown, unused: string, url?: string | URL | null) => void };
  location: { href: string; origin: string };
  name: string;
  opener: { postMessage: (message: unknown, targetOrigin: string) => void } | null;
};

export type OAuthLaunchResult =
  | { kind: "redirect" }
  | { kind: "popup"; popup: AuthWindow }
  | { kind: "refresh" };

export function oauthCompletionUrl(origin: string): string {
  const url = new URL(origin);
  url.searchParams.set(OAUTH_COMPLETION_PARAM, "complete");
  return url.href;
}

export function isStandaloneDisplayMode(matchMedia: (query: string) => { matches: boolean } = window.matchMedia.bind(window)): boolean {
  return matchMedia("(display-mode: standalone)").matches;
}

export async function launchGoogleOAuth({
  standalone,
  requestSignIn,
  openAuthWindow,
  navigateCurrent
}: {
  standalone: boolean;
  requestSignIn: () => Promise<{ url?: string }>;
  openAuthWindow: (url: string, target: string, features: string) => AuthWindow | null;
  navigateCurrent: (url: string) => void;
}): Promise<OAuthLaunchResult> {
  // This must happen before the first await so Safari treats it as user initiated.
  const popup = standalone
    ? openAuthWindow("about:blank", GOOGLE_AUTH_WINDOW_NAME, "popup,width=520,height=720")
    : null;

  let result: { url?: string };
  try {
    result = await requestSignIn();
  } catch (error) {
    popup?.close();
    throw error;
  }

  if (!result.url) {
    popup?.close();
    return { kind: "refresh" };
  }

  if (popup && !popup.closed) {
    try {
      popup.location.href = result.url;
      popup.focus?.();
      return { kind: "popup", popup };
    } catch {
      popup.close();
    }
  }

  navigateCurrent(result.url);
  return { kind: "redirect" };
}

export function consumeOAuthCompletion(browser: CompletionWindow = window): boolean {
  const url = new URL(browser.location.href);
  if (url.searchParams.get(OAUTH_COMPLETION_PARAM) !== "complete") return false;

  url.searchParams.delete(OAUTH_COMPLETION_PARAM);
  browser.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  if (!browser.opener) {
    if (browser.name !== GOOGLE_AUTH_WINDOW_NAME) return false;
    browser.close();
    return true;
  }

  browser.opener.postMessage({ type: OAUTH_COMPLETION_MESSAGE }, browser.location.origin);
  browser.close();
  return true;
}

export function isOAuthCompletionMessage(event: Pick<MessageEvent, "data" | "origin">, origin: string): boolean {
  return event.origin === origin
    && typeof event.data === "object"
    && event.data !== null
    && (event.data as { type?: unknown }).type === OAUTH_COMPLETION_MESSAGE;
}

export function watchOAuthPopup(
  popup: Pick<AuthWindow, "closed">,
  onClosed: () => void,
  timers: Pick<typeof globalThis, "setInterval" | "clearInterval"> = globalThis
): () => void {
  const interval = timers.setInterval(() => {
    if (!popup.closed) return;
    timers.clearInterval(interval);
    onClosed();
  }, 250);
  return () => timers.clearInterval(interval);
}
